// src/components/UserProfileModal.jsx
import React, { useMemo } from "react";
import { Modal, Form, Input, Row, Col } from "antd";
import axios from "axios";
import { BACKEND_URL } from "../../config/config";
import ApiKeyPanel from "../ApiKeyPanel/ApiKeyPanel.jsx";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - user: object (full user)
 * - onUpdated: (updatedUser) => void   // callback to update UI/localStorage
 */
export default function UserProfileModal({ open, onClose, user, onUpdated }) {
  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    if (!user) return {};
    return {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      title: user.title || "",
      description: user.description || "",
      street: user.street || "",
      suite: user.suite || "",
      apartment: user.apartment || "",
      city: user.city || "",
      state: user.state || "",
      billBoxNumber: user.billBoxNumber || "",
      electronicEmailBill: user.electronicEmailBill || "",
      specialBillingInstructions: user.specialBillingInstructions || "",
      emailMain: user?.email?.main || "",
      emailSecondary: user?.email?.secondary || "",
      phoneMain: user?.phone?.main || "",
      phoneMobile: user?.phone?.mobile || "",
      phoneFax: user?.phone?.fax || "",
      viewModePortal: user?.viewModePortal || "FullView",
      hasPortalAccess: !!user?.hasPortalAccess,
      sendPDFAttachments: !!user?.sendPDFAttachments,
      doNotNotifyViaEmail: !!user?.doNotNotifyViaEmail,
    };
  }, [user]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Build payload consolidating nested fields:
      const payload = {
        _id: user?._id,
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        title: values.title,
        description: values.description,
        street: values.street,
        suite: values.suite,
        apartment: values.apartment,
        city: values.city,
        state: values.state,
        billBoxNumber: values.billBoxNumber,
        electronicEmailBill: values.electronicEmailBill,
        specialBillingInstructions: values.specialBillingInstructions,
        viewModePortal: values.viewModePortal,
        hasPortalAccess: values.hasPortalAccess,
        sendPDFAttachments: values.sendPDFAttachments,
        doNotNotifyViaEmail: values.doNotNotifyViaEmail,
        email: {
          ...(user?.email || {}),
          main: values.emailMain,
          secondary: values.emailSecondary,
        },
        phone: {
          ...(user?.phone || {}),
          main: values.phoneMain,
          mobile: values.phoneMobile,
          fax: values.phoneFax,
        },
      };

      // 🔧 Ajustá el endpoint si tu backend usa otro:
      const url = `${BACKEND_URL}/customers/updateCustomer`;
      const { data } = await axios.post(url, payload);

      // Asumimos que el backend devuelve el user actualizado en data.user || data
      const updated = data?.user || data || payload;

      if (typeof onUpdated === "function") onUpdated(updated);
      onClose?.();
    } catch (err) {
      // Si falla validateFields no hacemos nada; si es error HTTP, podrías mostrar notificación
      console.error("Update profile error:", err);
    }
  };

  return (
    <Modal
      title="Edit Profile"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="Save"
      cancelText="Cancel"
      width={900}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="First Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="Last Name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Title" name="title">
              <Input placeholder="Title" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Description" name="description">
              <Input.TextArea placeholder="Notes / Description" rows={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Email (Main)"
              name="emailMain"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@example.com" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Email (Secondary)"
              name="emailSecondary"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="secondary@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Phone (Main)" name="phoneMain">
              <Input placeholder="(xxx) xxx-xxxx" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Mobile" name="phoneMobile">
              <Input placeholder="(xxx) xxx-xxxx" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Fax" name="phoneFax">
              <Input placeholder="(xxx) xxx-xxxx" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Street" name="street">
              <Input placeholder="Street" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Apartment" name="apartment">
              <Input placeholder="Apartment" disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Suite" name="suite">
              <Input placeholder="Suite" disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="City" name="city">
              <Input placeholder="City" disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="State" name="state">
              <Input placeholder="State" disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
        <ApiKeyPanel contactId={user?._id} />
      </div>
    </Modal>
  );
}
